import type { User, UserRole } from '@/types'
import { useAtom, useAtomValue } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getUsers, putAdmin, putRole, putUser } from '@/api/user'
import { reportError } from '@/lib/client/error'
import { collectionSince, reconcileCollection } from '@/lib/incremental'
import { validIdTokenAtom } from '@/pages/state'
import { adminUsersAtom, adminUsersRefreshedAtAtom } from './atoms'

/**
 * The backend refreshes a user's `lastSeen` at most once every 15 minutes (lambda/lib/auth.ts), so
 * asking for the list more often than that cannot show anything new.
 */
const LAST_SEEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000

export const useAdminUserActions = () => {
  const token = useAtomValue(validIdTokenAtom)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [users, setUsers] = useAtom(adminUsersAtom)

  const replaceUser = (user: User) => {
    const oldIndex = users.findIndex((u) => u.id === user.id)
    const newUsers = [...users]
    newUsers.splice(oldIndex === -1 ? users.length : oldIndex, oldIndex === -1 ? 0 : 1, user)
    void setUsers(newUsers)
  }

  // Reads the list through the store rather than through `users`, so the callback stays stable and
  // callers can refresh on mount without the refresh itself scheduling the next one.
  const refreshUsers = useAtomCallback(
    useCallback(async (get, set, token: string) => {
      const now = Date.now()
      if (now - get(adminUsersRefreshedAtAtom) < LAST_SEEN_REFRESH_INTERVAL_MS) return

      // The cached list is complete up to its own newest timestamp, so only what happened after it
      // has to come over the wire. Without this a mount refresh discards the cache every time.
      const current = await get(adminUsersAtom)
      const since = collectionSince(current)
      const response = since ? await getUsers(token, undefined, since) : await getUsers(token)
      await set(adminUsersAtom, (latest) => reconcileCollection(latest, response))
      // Only once it succeeded: a failed refresh should be retried, not waited out.
      set(adminUsersRefreshedAtAtom, now)
    }, [])
  )

  const refresh = useCallback(async () => {
    if (!token) return
    try {
      await refreshUsers(token)
    } catch (e) {
      reportError(e)
    }
  }, [token, refreshUsers])

  return {
    addRole: async (user: User, orgId: string, role: UserRole) => {
      try {
        const saved = await putRole({ orgId, role, userId: user.id }, token)
        replaceUser(saved)
      } catch (e) {
        reportError(e)
      }
    },
    addUser: async (user: User, organizerName: string) => {
      try {
        const added = await putUser(user, token)
        replaceUser(added)
        if (user.name === added.name) {
          enqueueSnackbar(t('user.added', { email: added.email, name: added.name }), { variant: 'info' })
        } else {
          enqueueSnackbar(t('user.roleAdded', { email: added.email, name: added.name, organizer: organizerName }), {
            variant: 'info',
          })
        }
      } catch (e) {
        reportError(e)
      }
    },
    refresh,
    removeRole: async (user: User, orgId: string) => {
      try {
        const saved = await putRole({ orgId, role: 'none', userId: user.id }, token)
        replaceUser(saved)
      } catch (e) {
        reportError(e)
      }
    },
    setAdmin: async (user: User) => {
      try {
        const saved = await putAdmin({ admin: !!user.admin, userId: user.id }, token)
        replaceUser(saved)
      } catch (e) {
        reportError(e)
      }
    },
  }
}
